import {
  faArrowDownToLine,
  faArrowsFromLine,
  faArrowsToLine,
  faArrowUpRightFromSquare,
  faCopy,
  faFileCsv,
  faPause,
  faPlay,
  faPlus,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SearchIcon from '@mui/icons-material/Search';
import {
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import InputAdornment from '@mui/material/InputAdornment';
import get from 'lodash-es/get';
import React, { useEffect, useState } from 'react';
import { generatePath, useLocation, useNavigate, useParams } from 'react-router-dom';

import { truncatedTextStyle } from '../../constants/contractConfigForms';
import {
  ADMIN_CONTRACT_EDIT_URL,
  ADMIN_CONTRACT_NEW_URL,
  EDIT_LINE_ITEM_URL,
  NEW_LINE_ITEM_URL,
} from '../../constants/routeUrls';
import { useConfig } from '../../hooks/useConfig';
import useConfirm from '../../hooks/useConfirm';
import { useContractConfigurationContext } from '../../hooks/useContractConfigurationContext';
import useLineItems from '../../hooks/useLineItems';
import useModal from '../../hooks/useModal';
import usePauseAllContracts from '../../hooks/usePauseAllContracts';
import useResumeAllContracts from '../../hooks/useResumeAllContracts';
import { useSnackBar } from '../../hooks/useSnackBar';
import { Link } from '../../inmarkit';
import { activeBackgroundColor, hoverBackgroundColor } from '../../styles/styles';
import BulkEdit from '../BulkEdit/BulkEditForm';
import DuplicateContractForm from '../Contracts/DuplicateContractForm';
import LineItemNav from './LineItemNav';

interface ContractNavProps {
  contract: any;
}

function ContractNav({ contract }: ContractNavProps) {
  const params = useParams();
  const contractId = params.id;
  const isEditMode = !!contractId;
  const createNewLineItemUrl = generatePath(NEW_LINE_ITEM_URL, { id: contractId || '' });
  const editContractUrl = generatePath(ADMIN_CONTRACT_EDIT_URL, { id: contractId || '' });
  const newContractUrl = generatePath(ADMIN_CONTRACT_NEW_URL);
  const { pathname } = useLocation();
  const isHighlightContract = [createNewLineItemUrl, editContractUrl, newContractUrl].includes(pathname);

  const { apiHost } = useConfig();
  const [expandAll, setExpandAll] = useState(true);
  const [filter, setFilter] = useState('');
  const navigate = useNavigate();
  const lineItems = useLineItems(Number(contractId));
  const { showSnackBar } = useSnackBar();
  const { has_unpaused_campaigns: initialHasUnpausedCampaigns } = contract;
  const contractConfiguration = useContractConfigurationContext();
  const [isHovered, setHovered] = useState(false);

  const [hasUnpausedCampaigns, setHasUnpausedCampaigns] = useState(initialHasUnpausedCampaigns);

  useEffect(() => {
    setHasUnpausedCampaigns(initialHasUnpausedCampaigns);
  }, [initialHasUnpausedCampaigns]);
  const refetchContract = get(contractConfiguration, 'refetch');
  const isRefetching = get(contractConfiguration, 'isRefetching');
  const contractName = get(contractConfiguration, 'contractName');

  const [showDuplicateContractForm, closeDuplicateContractForm] = useModal(
    () => <DuplicateContractForm contract={contract} onClose={closeDuplicateContractForm} />,
    null,
    [contract]
  );

  const pauseAllMutation = usePauseAllContracts(contractId);
  const resumeAllMutation = useResumeAllContracts(contractId);

  const { windowDotConfirm } = useConfirm(
    `Are you sure you want to ${hasUnpausedCampaigns ? 'pause' : 'resume'} the contract "${contractName}"?`
  );

  const goToContractPage = () => isEditMode && navigate(generatePath(ADMIN_CONTRACT_EDIT_URL, { id: contractId }));

  const togglePause = async () => {
    if (await windowDotConfirm()) {
      if (hasUnpausedCampaigns) {
        pauseAllMutation.mutate(undefined, {
          onSuccess: () => {
            setHasUnpausedCampaigns(false);
            // Even tho we have updated the UI, we should refetch for good measure
            // to update the state in the contract configuration context.
            refetchContract();
            showSnackBar(`Contract ${contractName} was successfully paused.`, 'success', {
              event: 'contract_paused',
              contract_id: contractId,
              contract_name: contractName,
            });
          },
          onError: () => {
            showSnackBar(`Contract ${contractName} failed to pause.`, 'error', {
              event: 'contract_pause_error',
              contract_id: contractId,
              contract_name: contractName,
            });
          },
        });
      } else {
        resumeAllMutation.mutate(undefined, {
          onSuccess: () => {
            setHasUnpausedCampaigns(true);
            // Even tho we have updated the UI, we should refetch for good measure
            // to update the state in the contract configuration context.
            refetchContract();
            showSnackBar(`Contract ${contractName} was successfully resumed.`, 'success', {
              event: 'contract_resumed',
              contract_id: contractId,
              contract_name: contractName,
            });
          },
          onError: () => {
            showSnackBar(`Contract ${contractName} failed to resumed.`, 'error', {
              event: 'contract_resume_error',
              contract_id: contractId,
              contract_name: contractName,
            });
          },
        });
      }
    }
  };

  const [openBulkEditForm, closeBulkEditForm] = useModal(() => <BulkEdit onClose={closeBulkEditForm} />);

  const toggleExpand = () => {
    if (expandAll === undefined) {
      setExpandAll(true);
    } else {
      setExpandAll(!expandAll);
    }
  };

  const openContractInNewTab = () => window.open(generatePath(ADMIN_CONTRACT_EDIT_URL, { id: contractId }));

  return (
    <List component="nav" aria-label="line item navigation" sx={{ overflow: 'auto' }}>
      <ListItem divider>
        <Stack spacing={1} sx={{ width: '100%' }}>
          <Stack direction="row" sx={{ width: '100%' }}>
            <TextField
              size="small"
              fullWidth
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <Stack direction="row" spacing={1} sx={{ width: '100%', alignItems: 'center' }}>
            <Tooltip title="Bulk Edit">
              <IconButton size="small" onClick={openBulkEditForm}>
                <FontAwesomeIcon icon={faFileCsv} />
              </IconButton>
            </Tooltip>
            <Tooltip title={`${expandAll ? 'Collapse' : 'Expand'} All`}>
              <IconButton size="small" onClick={toggleExpand}>
                <>
                  {expandAll && <FontAwesomeIcon icon={faArrowsToLine} />}
                  {!expandAll && <FontAwesomeIcon icon={faArrowsFromLine} />}
                </>
              </IconButton>
            </Tooltip>
          </Stack>
        </Stack>
      </ListItem>
      <Tooltip title={contractName && `${contractName} ${contractId ? `(${contractId})` : ''}`} placement="right">
        <ListItem
          divider
          sx={{
            '&:hover': {
              backgroundColor: isHighlightContract ? activeBackgroundColor : hoverBackgroundColor,
            },
            backgroundColor: isHighlightContract ? activeBackgroundColor : 'white',
          }}
          onMouseOver={() => setHovered(true)}
          onMouseOut={() => setHovered(false)}
        >
          <ListItemText
            primary={
              <Typography
                fontWeight="600"
                sx={{
                  cursor: isEditMode && 'pointer',
                }}
                className="side-bar-item-title"
              >
                {contractName} {contractId ? `(${contractId})` : ''}
              </Typography>
            }
            primaryTypographyProps={{
              sx: truncatedTextStyle,
            }}
            sx={{ flexBasis: 0 }}
            onClick={goToContractPage}
          />
          <Stack
            direction="row"
            spacing={1}
            ml={1}
            sx={{
              display: isHovered && isEditMode ? 'inherit' : 'none',
            }}
          >
            <Tooltip title="Add Line Item" placement="top">
              <IconButton size="small" onClick={() => navigate(createNewLineItemUrl)} color="primary">
                <FontAwesomeIcon icon={faPlus} />
              </IconButton>
            </Tooltip>
            <Tooltip title={hasUnpausedCampaigns ? 'Pause Contract' : 'Resume Contract'} placement="top">
              <IconButton size="small" onClick={togglePause}>
                {!isRefetching && (
                  <>
                    {hasUnpausedCampaigns && <FontAwesomeIcon icon={faPause} />}
                    {!hasUnpausedCampaigns && <FontAwesomeIcon icon={faPlay} />}
                  </>
                )}
                {isRefetching && <CircularProgress color="inherit" size={20} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Duplicate Contract" placement="top">
              <IconButton size="small" onClick={showDuplicateContractForm}>
                <FontAwesomeIcon icon={faCopy} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download Contract IDs (CSV)" placement="top">
              <IconButton size="small">
                <Link
                  target="_blank"
                  href={`${apiHost}/admin/contracts/${contractId}/child_metadata.csv`}
                  sx={{ color: 'gray' }}
                >
                  <FontAwesomeIcon icon={faArrowDownToLine} />
                </Link>
              </IconButton>
            </Tooltip>
            <Tooltip title="Open in new tab" placement="top">
              <IconButton size="small" onClick={openContractInNewTab}>
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
              </IconButton>
            </Tooltip>
          </Stack>
        </ListItem>
      </Tooltip>
      {isEditMode && lineItems.isLoading && (
        <Skeleton data-testid="line-item-list-skeleton" variant="rectangular" height={50} />
      )}
      {isEditMode &&
        lineItems.isSuccess &&
        lineItems.data.collection?.map((lineItem) => (
          <LineItemNav
            key={lineItem.id}
            lineItem={lineItem}
            filter={filter}
            expand={expandAll}
            onClick={() =>
              navigate(generatePath(EDIT_LINE_ITEM_URL, { id: contractId, lineItemId: lineItem.id.toString() }))
            }
          />
        ))}
    </List>
  );
}

export default ContractNav;
